import { Injectable } from '@angular/core';
import { Store, StoreConfig } from '@datorama/akita';
import { iStoreActions } from '../../loan-state/core/store-actions.interface';
import { Note } from '../components/notes-panel-v2/models/note.model';

/**
 * State interface for NoteListStore.
 *
 * Manages:
 * - Notes collection (for display and MVP 2 sharing across components)
 * - Editor state (for dirty checking / unsaved changes modal)
 */
export interface NoteListState {
  /** Collection of notes for the current entity */
  notes: Note[];

  /** The entity's fileDataId (loan) or GUID (lead/application) */
  entityId: number | string | null;

  /** Editor content - tracked for dirty checking */
  editorContent: string;

  /** GUID of note being edited (null if creating new or editing legacy) */
  editingNoteGuid: string | null;

  /**
   * Whether the note being edited is a legacy note (from LoanColorMapping).
   * Legacy notes don't have GUIDs and use V1 API for save.
   */
  isEditingLegacyNote: boolean;

  /** Selected tag GUIDs for the note being edited (for tag sync on save) */
  selectedTagGuids: string[];
}

function createInitialState(): NoteListState {
  return {
    notes: [],
    entityId: null,
    editorContent: '',
    editingNoteGuid: null,
    isEditingLegacyNote: false,
    selectedTagGuids: [],
  };
}

/**
 * NoteListStore - Manages state for Notes Panel V2.
 *
 * Used for:
 * - Storing notes collection (for display and MVP 2 cross-component sharing)
 * - Tracking editor state for dirty checking (unsaved changes modal)
 *
 * This store is generic and can be reused for Loans, Leads, and Applications
 * when they migrate to V2 notes.
 */
@Injectable({ providedIn: 'root' })
@StoreConfig({ name: 'note-list' })
export class NoteListStore extends Store<NoteListState> implements iStoreActions<NoteListState> {
  private _initValue: NoteListState = null;

  constructor() {
    super(createInitialState());
  }

  /**
   * Called before initialization to capture the initial state.
   * Required by iStoreActions for DirtyChecker2 comparison.
   */
  preInitialize(): void {
    this._initValue = JSON.parse(JSON.stringify(this.getValue()));
  }

  /**
   * Returns the initial state for dirty checking comparison.
   */
  get initialValue(): NoteListState {
    return this._initValue;
  }

  /**
   * Updates the editor content (triggers dirty checking).
   */
  updateEditorContent(content: string): void {
    this.update({ editorContent: content });
  }

  /**
   * Opens editor for creating a new note.
   */
  openCreateEditor(): void {
    this.resetEditor();
  }

  /**
   * Opens editor for editing an existing note.
   * @param noteGuid - GUID of the note (undefined for legacy notes)
   * @param content - Current content of the note
   * @param isLegacy - Whether this is a legacy note from LoanColorMapping
   * @param tagGuids - Existing tag GUIDs on the note
   */
  openEditEditor(noteGuid: string | undefined, content: string, isLegacy: boolean = false, tagGuids: string[] = []): void {
    this.update({
      editorContent: content,
      editingNoteGuid: noteGuid ?? null,
      isEditingLegacyNote: isLegacy,
      selectedTagGuids: tagGuids,
    });
  }

  /**
   * Resets editor state (on cancel/close).
   */
  resetEditor(): void {
    this.update({
      editorContent: '',
      editingNoteGuid: null,
      isEditingLegacyNote: false,
      selectedTagGuids: [],
    });
  }

  /**
   * Updates selected tag GUIDs for the note being edited.
   */
  updateSelectedTagGuids(guids: string[]): void {
    this.update({ selectedTagGuids: guids });
  }
}
